<template>
    <div id="page-content-wrapper" class="admin-content-area">
        <div class="container-fluid xyz">
            <div class="admin-page-content">
                <div class="page-title-container">
                    <div class="page-name">{{ isEditMode ? 'Edit Stock Build Request' : 'Stock Build Request Form' }}</div>
                    <button type="button" class="ml-2 btn btn-primary pull-right themebutton" v-on:click.prevent="goToList">
                        <span>Back</span>
                    </button>
                </div>

                <div class="forms-container approval-form-shell">
                    <div v-if="successMessage" class="approval-alert approval-alert-success">
                        {{ successMessage }}
                    </div>
                    <div v-if="errorMessage" class="approval-alert approval-alert-error">
                        {{ errorMessage }}
                    </div>

                    <div v-if="isLoading" class="approval-helper-text">
                        Loading request details...
                    </div>

                    <p v-else-if="isEditMode && !accessDenied" class="approval-helper-text">
                        Only the Unit ID and Commission Number can be changed. Every other field is read-only.
                    </p>

                    <form v-if="!isLoading && !accessDenied" v-on:submit.prevent="submitForm">
                        <div class="row approval-form-grid">
                            <div class="col-12 col-md-6 approval-field">
                                <label for="Dealership">Dealership <span>*</span></label>
                                <select id="Dealership" v-model="form.Dealership" class="form-control" tabindex="1" :disabled="isEditMode">
                                    <option value="">Select dealership</option>
                                    <option v-for="option in dealershipOptions" :key="option" :value="option">{{ option }}</option>
                                </select>
                                <div class="help is-danger">{{ fieldError('Dealership') }}</div>
                            </div>

                            <div class="col-12 col-md-6 approval-field">
                                <label for="Make">Make <span>*</span></label>
                                <select id="Make" v-model="form.Make" class="form-control" tabindex="2" :disabled="isEditMode">
                                    <option value="">Select make</option>
                                    <option v-for="option in makeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                                </select>
                                <div class="help is-danger">{{ fieldError('Make') }}</div>
                            </div>

                            <div class="col-12 col-md-6 approval-field">
                                <label for="Series">Series <span>*</span></label>
                                <select id="Series" v-model="form.Series" class="form-control" tabindex="3" :disabled="isEditMode">
                                    <option value="">Select series</option>
                                    <option v-for="option in seriesOptions" :key="option" :value="option">{{ option }}</option>
                                </select>
                                <div class="help is-danger">{{ fieldError('Series') }}</div>
                            </div>

                            <div class="col-12 col-md-6 approval-field">
                                <label for="ModelDescription">Model Description <span>*</span></label>
                                <input id="ModelDescription" v-model="form.ModelDescription" type="text" class="form-control" placeholder="Enter model description" maxlength="255" tabindex="4" :readonly="isEditMode">
                                <div class="help is-danger">{{ fieldError('ModelDescription') }}</div>
                            </div>

                            <div class="col-12 col-md-6 approval-field">
                                <label for="UnitId">Unit ID <span v-if="isEditMode">*</span></label>
                                <input id="UnitId" v-model="form.UnitId" type="text" class="form-control" placeholder="Enter unit ID" maxlength="6" tabindex="5" v-on:input="form.UnitId = form.UnitId.replace(/[^0-9]/g, '')">
                                <div class="help is-danger">{{ fieldError('UnitId') }}</div>
                            </div>

                            <div class="col-12 col-md-6 approval-field">
                                <label for="CommissionNumber">Commission Number <span v-if="isEditMode">*</span></label>
                                <input id="CommissionNumber" v-model="form.CommissionNumber" type="text" class="form-control" placeholder="Enter commission number" maxlength="100" tabindex="6">
                                <div class="help is-danger">{{ fieldError('CommissionNumber') }}</div>
                            </div>

                            <div class="col-12 col-md-6 approval-field">
                                <label for="BodyType">Body Type <span>*</span></label>
                                <input id="BodyType" v-model="form.BodyType" type="text" class="form-control" placeholder="Enter body type" maxlength="100" tabindex="7" :readonly="isEditMode">
                                <div class="help is-danger">{{ fieldError('BodyType') }}</div>
                            </div>

                            <div class="col-12 col-md-6 approval-field">
                                <label for="TargetCustomer">Target Customer <span>*</span></label>
                                <input id="TargetCustomer" v-model="form.TargetCustomer" type="text" class="form-control" placeholder="Enter target customer" maxlength="150" tabindex="8" :readonly="isEditMode">
                                <div class="help is-danger">{{ fieldError('TargetCustomer') }}</div>
                            </div>

                            <div class="col-12 col-md-6 approval-field">
                                <label for="SubmittedByName">Submitter Name</label>
                                <input id="SubmittedByName" v-model="form.SubmittedByName" type="text" class="form-control" placeholder="Enter submitter name" maxlength="150" readonly tabindex="9">
                                <div class="help is-danger">{{ fieldError('SubmittedByName') }}</div>
                            </div>

                            <div class="col-12 col-md-6 approval-field">
                                <label for="SubmittedBy">Submitter Email</label>
                                <input id="SubmittedBy" v-model="form.SubmittedBy" type="email" class="form-control" placeholder="name@example.com" maxlength="150" readonly tabindex="10">
                                <div class="help is-danger">{{ fieldError('SubmittedBy') }}</div>
                            </div>

                            <div v-if="isEditMode" class="col-12 approval-field">
                                <label>Body Quote</label>
                                <ul v-if="existingQuotes.length" class="quote-list">
                                    <li v-for="(quote, index) in existingQuotes" :key="index">
                                        <a :href="quote.url" target="_blank" rel="noopener">{{ quote.name }}</a>
                                    </li>
                                </ul>
                                <div v-else class="quote-hint">No body quotes attached.</div>
                            </div>

                            <div v-else class="col-12 approval-field">
                                <label for="BodyQuotes">Body Quote <span>*</span></label>
                                <input id="BodyQuotes" ref="bodyQuotes" type="file" class="form-control quote-input" accept="application/pdf,.pdf" multiple tabindex="11" v-on:change="onQuotesSelected">
                                <div class="quote-hint">Attach up to {{ maxQuoteFiles }} PDF body quotes (max {{ maxQuoteSizeMb }}MB each). You can add them together or one at a time &mdash; {{ bodyQuotes.length }} of {{ maxQuoteFiles }} attached.</div>
                                <ul v-if="bodyQuotes.length" class="quote-list">
                                    <li v-for="(file, index) in bodyQuotes" :key="index">
                                        {{ file.name }}
                                        <a href="#" class="quote-remove" v-on:click.prevent="removeQuote(index)">Remove</a>
                                    </li>
                                </ul>
                                <div class="help is-danger">{{ fieldError('BodyQuotes') }}</div>
                            </div>

                            <div class="col-12 approval-field">
                                <label for="StockBuildExplanation">Stock Build Request Explanation <span>*</span></label>
                                <textarea id="StockBuildExplanation" v-model="form.StockBuildExplanation" rows="5" class="form-control no-resize" placeholder="Explain why this stock build is to be approved" :maxlength="maxExplanationLength" tabindex="12" :readonly="isEditMode"></textarea>
                                <div class="quote-hint">{{ explanationLength }} / {{ maxExplanationLength }} characters</div>
                                <div class="help is-danger">{{ fieldError('StockBuildExplanation') }}</div>
                            </div>

                            <div class="col-12 approval-actions">
                                <button v-if="!isEditMode" type="button" class="ml-2 btn btn-primary pull-right themebutton cancelbutton" tabindex="14" v-on:click="resetForm" :disabled="isSubmitting">
                                    <span>Reset</span>
                                </button>
                                <button type="submit" class="ml-2 btn btn-primary pull-right themebutton" tabindex="13" :disabled="isSubmitting">
                                    <span>{{ submitLabel }}</span>
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            <app-footer></app-footer>
        </div>
    </div>
</template>

<script>
    import Footer from '@/components/partials/Footer';
    import {HTTP} from '../../http-common.js';

    export default {
        name: 'StockBuildRequestFormSubmission',
        components: {
            'app-footer': Footer,
        },
        data() {
            return {
                form: this.defaultForm(),
                bodyQuotes: [],
                errors: {},
                successMessage: '',
                errorMessage: '',
                isSubmitting: false,
                isLoading: false,
                // Set when the API refuses the edit (not an approver, or the
                // request is already approved) so the form is never rendered.
                accessDenied: false,
                existingQuotes: [],
                maxQuoteFiles: 3,
                maxQuoteSizeMb: 10,
                maxExplanationLength: 4000,
                dealershipOptions: [
                    'Daimler Trucks Brisbane',
                    'Daimler Trucks Gold Coast',
                    'Daimler Trucks Newcastle',
                    'Daimler Trucks Huntingwood',
                    'Daimler Trucks Milperra',
                    'Daimler Trucks Dandenong',
                    'Fuso Port Melbourne',
                    'Daimler Trucks Laverton',
                    'Daimler Trucks Geelong',
                    'Daimler Trucks Ballarat',
                    'Daimler Trucks Somerton',
                    'Daimler Trucks Adelaide',
                    'Fuso Adelaide',
                    'Mildura',
                    'Mt Gambier',
                    'Daimler Trucks Perth',
                ],
                makeOptions: [
                    { value: 'MB', text: 'MB' },
                    { value: 'FL', text: 'FL' },
                    { value: 'FUSO', text: 'FUSO' },
                ],
                seriesOptionsByMake: {
                    MB: [
                        'ACTROS',
                        'ATEGO',
                        'AROCS',
                        'ECONIC',
                        'UNIMOG',
                        'eACTROS',
                        'eECONIC',
                        'eATEGO',
                    ],
                    FL: [
                        'CASCADIA',
                    ],
                    FUSO: [
                        'CANTER',
                        'FIGHTER',
                        'SHOGUN',
                        'ROSA',
                        'eCANTER',
                    ],
                },
            };
        },
        computed: {
            seriesOptions() {
                return this.seriesOptionsByMake[this.form.Make] || [];
            },
            explanationLength() {
                return (this.form.StockBuildExplanation || '').length;
            },
            formId() {
                return this.$route.params.id || '';
            },
            isEditMode() {
                return !!this.formId;
            },
            submitLabel() {
                if (this.isEditMode) {
                    return this.isSubmitting ? 'Updating...' : 'Update';
                }

                return this.isSubmitting ? 'Submitting...' : 'Submit For Approval';
            },
        },
        watch: {
            'form.Make': function () {
                if (this.seriesOptions.indexOf(this.form.Series) === -1) {
                    this.form.Series = '';
                }
                this.selectDefaultSeriesIfNeeded();
            },
        },
        mounted() {
            if (this.isEditMode) {
                this.loadForEdit();
                return;
            }

            this.form.SubmittedByName = this.submitterName();
            this.form.SubmittedBy = this.submitterEmail();
            this.selectDefaultSeriesIfNeeded();
        },
        methods: {
            authConfig() {
                return {
                    headers: {Authorization: "Bearer " + localStorage.getItem("tkn")},
                };
            },
            loadForEdit() {
                var self = this;
                self.isLoading = true;

                HTTP.get('/stock-build-request-forms/edit/' + self.formId, self.authConfig()).then(function (response) {
                    var data = response.data && response.data.data ? response.data.data : null;

                    if (!data) {
                        self.accessDenied = true;
                        self.errorMessage = 'Unable to load this request.';
                        return;
                    }

                    Object.keys(self.form).forEach(function (key) {
                        if (data[key] !== undefined && data[key] !== null) {
                            self.form[key] = data[key];
                        }
                    });

                    self.existingQuotes = response.data.quoteLinks || [];
                }).catch(function (err) {
                    self.accessDenied = true;
                    self.errorMessage = self.responseMessage(err, 'Unable to load this request.');

                    if (err.response && err.response.status === 401) {
                        self.redirectToLogin();
                    }
                }).then(function () {
                    self.isLoading = false;
                });
            },
            responseMessage(err, fallback) {
                if (err.response && err.response.data && err.response.data.message) {
                    return err.response.data.message;
                }

                return fallback;
            },
            redirectToLogin() {
                var currentBuild = window.localStorage.getItem('build_version');
                localStorage.clear();
                localStorage.setItem('build_version', currentBuild);
                alert('Your session has expired. You will be redirected to the login page.');
                window.location.href = process.env.SSO_ROOT_URL + '/login?redirect=' + process.env.FRONT_ROOT_URL + '&slug=' + process.env.PROJECT_SLUG;
            },
            validateEditForm() {
                var errors = {};

                ['UnitId', 'CommissionNumber'].forEach((field) => {
                    if (this.form[field] === null || String(this.form[field] || '').trim() === '') {
                        errors[field] = ['This field is required.'];
                    }
                });

                this.errors = errors;
                return Object.keys(errors).length === 0;
            },
            updateForm() {
                var self = this;

                self.successMessage = '';
                self.errorMessage = '';
                self.errors = {};

                if (!self.validateEditForm()) {
                    self.errorMessage = 'Please complete the required fields.';
                    return;
                }

                self.isSubmitting = true;

                HTTP.post('/stock-build-request-forms/update/' + self.formId, {
                    UnitId: self.form.UnitId,
                    CommissionNumber: self.form.CommissionNumber,
                }, self.authConfig()).then(function () {
                    self.$router.push({ name: 'StockBuildRequestFormList', query: { success: 'Stock Build Request updated successfully.' } });
                }).catch(function (err) {
                    if (err.response && err.response.status === 401) {
                        self.redirectToLogin();
                        return;
                    }

                    if (err.response && err.response.data && err.response.data.errors) {
                        self.errors = err.response.data.errors;
                        self.errorMessage = 'Please review the highlighted fields.';
                    } else {
                        self.errorMessage = self.responseMessage(err, 'Unable to update the request. Please try again.');
                    }
                }).then(function () {
                    self.isSubmitting = false;
                });
            },
            defaultForm() {
                return {
                    Dealership: '',
                    Make: '',
                    Series: '',
                    ModelDescription: '',
                    UnitId: '',
                    CommissionNumber: '',
                    BodyType: '',
                    TargetCustomer: '',
                    StockBuildExplanation: '',
                    SubmittedByName: '',
                    SubmittedBy: '',
                };
            },
            submitterName() {
                try {
                    var user = JSON.parse(localStorage.getItem('user_obj') || '{}');
                    var firstName = user.first_name || '';
                    var lastName = user.last_name || '';
                    var fullName = (firstName + ' ' + lastName).trim();

                    return fullName || user.username || '';
                } catch (e) {
                    return '';
                }
            },
            submitterEmail() {
                try {
                    var user = JSON.parse(localStorage.getItem('user_obj') || '{}');
                    return user.username || '';
                } catch (e) {
                    return '';
                }
            },
            selectDefaultSeriesIfNeeded() {
                if (this.seriesOptions.length === 1) {
                    this.form.Series = this.seriesOptions[0];
                }
            },
            fieldError(field) {
                if (this.errors[field]) {
                    return this.errors[field][0];
                }

                // Per-file upload errors come back keyed as BodyQuotes.0, BodyQuotes.1 ...
                if (field === 'BodyQuotes') {
                    var fileKeys = Object.keys(this.errors).filter(function (key) {
                        return key.indexOf('BodyQuotes.') === 0;
                    });

                    if (fileKeys.length) {
                        return this.errors[fileKeys[0]][0];
                    }
                }

                return '';
            },
            onQuotesSelected(event) {
                var selected = Array.prototype.slice.call(event.target.files || []);
                var errors = Object.assign({}, this.errors);
                delete errors.BodyQuotes;

                // Each trip through the picker fires its own change event, so keep
                // what was chosen earlier: quotes can be added one at a time as
                // well as in a single multi-select.
                var combined = this.bodyQuotes.slice();

                for (var i = 0; i < selected.length; i++) {
                    var file = selected[i];

                    if (!/\.pdf$/i.test(file.name)) {
                        errors.BodyQuotes = ['Only PDF files can be attached.'];
                        break;
                    }

                    if (this.isOversized(file)) {
                        errors.BodyQuotes = ['Each PDF must be ' + this.maxQuoteSizeMb + 'MB or smaller.'];
                        break;
                    }

                    if (this.alreadySelected(combined, file)) {
                        continue;
                    }

                    if (combined.length >= this.maxQuoteFiles) {
                        errors.BodyQuotes = ['You can attach a maximum of ' + this.maxQuoteFiles + ' PDFs.'];
                        break;
                    }

                    combined.push(file);
                }

                this.errors = errors;
                this.bodyQuotes = combined;

                // Always clear the native input: without this the browser skips the
                // change event when the same file is picked again after a removal.
                this.clearQuoteInput();
            },
            alreadySelected(list, file) {
                return list.some(function (existing) {
                    return existing.name === file.name && existing.size === file.size;
                });
            },
            isOversized(file) {
                return file.size > this.maxQuoteSizeMb * 1024 * 1024;
            },
            removeQuote(index) {
                this.bodyQuotes.splice(index, 1);
                this.clearQuoteInput();
            },
            clearQuoteInput() {
                if (this.$refs.bodyQuotes) {
                    this.$refs.bodyQuotes.value = '';
                }
            },
            validateForm() {
                var errors = {};
                var requiredFields = ['Dealership', 'Make', 'Series', 'ModelDescription', 'BodyType', 'TargetCustomer', 'StockBuildExplanation'];

                requiredFields.forEach((field) => {
                    if (this.form[field] === null || this.form[field] === '') {
                        errors[field] = ['This field is required.'];
                    }
                });

                // maxlength stops typing at the cap, but a paste on older browsers can exceed it.
                if (this.explanationLength > this.maxExplanationLength) {
                    errors.StockBuildExplanation = ['Please keep the explanation to ' + this.maxExplanationLength + ' characters or fewer.'];
                }

                if (!this.bodyQuotes.length) {
                    errors.BodyQuotes = ['Please attach at least one body quote PDF.'];
                } else if (this.bodyQuotes.length > this.maxQuoteFiles) {
                    errors.BodyQuotes = ['You can attach a maximum of ' + this.maxQuoteFiles + ' PDFs.'];
                }

                this.errors = errors;
                return Object.keys(errors).length === 0;
            },
            payload() {
                var data = new FormData();
                var values = Object.assign({}, this.form);

                values.SubmittedByName = this.submitterName();
                values.SubmittedBy = this.submitterEmail();

                Object.keys(values).forEach(function (key) {
                    if (values[key] !== '' && values[key] !== null) {
                        data.append(key, values[key]);
                    }
                });

                this.bodyQuotes.forEach(function (file) {
                    data.append('BodyQuotes[]', file, file.name);
                });

                return data;
            },
            submitForm() {
                var self = this;

                if (self.isEditMode) {
                    self.updateForm();
                    return;
                }

                self.successMessage = '';
                self.errorMessage = '';

                if (!self.validateForm()) {
                    self.errorMessage = 'Please complete the required fields.';
                    return;
                }

                self.isSubmitting = true;

                var config = {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("tkn"),
                        'Content-Type': 'multipart/form-data',
                    },
                };

                HTTP.post('/stock-build-request-forms', self.payload(), config).then(function () {
                    self.$router.push({ name: 'StockBuildRequestFormList', query: { success: 'Stock Build Request Form submitted successfully.' } });
                }).catch(function (err) {
                    if (err.response && err.response.status === 401) {
                        var currentBuild = window.localStorage.getItem('build_version');
                        localStorage.clear();
                        localStorage.setItem('build_version', currentBuild);
                        alert('Your session has expired. You will be redirected to the login page.');
                        window.location.href = process.env.SSO_ROOT_URL + '/login?redirect=' + process.env.FRONT_ROOT_URL + '&slug=' + process.env.PROJECT_SLUG;
                        return;
                    }
                    if (err.response && err.response.data && err.response.data.errors) {
                        self.errors = err.response.data.errors;
                        self.errorMessage = 'Please review the highlighted fields.';
                    } else if (err.response && err.response.data && err.response.data.message) {
                        self.errorMessage = err.response.data.message;
                    } else {
                        self.errorMessage = 'Unable to submit the form. Please try again.';
                    }
                    self.isSubmitting = false;
                }).then(function () {
                    self.isSubmitting = false;
                });
            },
            goToList() {
                this.$router.push({ name: 'StockBuildRequestFormList' });
            },
            resetForm(keepSuccessMessage) {
                var successMessage = keepSuccessMessage === true ? this.successMessage : '';
                this.form = this.defaultForm();
                this.form.SubmittedByName = this.submitterName();
                this.form.SubmittedBy = this.submitterEmail();
                this.bodyQuotes = [];
                this.clearQuoteInput();
                this.errors = {};
                this.errorMessage = '';
                this.successMessage = successMessage;
            },
        },
    };
</script>

<style scoped>
    .approval-form-shell {
        background: #ffffff;
        border: 1px solid #d8e0e6;
        border-radius: 6px;
        box-shadow: 0 4px 16px rgba(31, 45, 61, 0.08);
        margin-top: 24px;
        padding: 34px 36px 28px;
    }

    .approval-form-grid {
        margin-left: -14px;
        margin-right: -14px;
    }

    .approval-field {
        margin-bottom: 22px;
        padding-left: 14px;
        padding-right: 14px;
    }

    .approval-field label {
        color: #2f3337;
        display: block;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 8px;
    }

    .approval-field label span {
        color: #d22f2f;
    }

    .approval-actions {
        padding-left: 14px;
        padding-right: 14px;
        padding-top: 6px;
    }

    .approval-form-shell .form-control {
        min-height: 42px;
    }

    .approval-form-shell textarea.form-control {
        min-height: 120px;
    }

    .quote-input {
        padding: 8px 10px;
    }

    .quote-hint {
        color: #6a7581;
        font-size: 13px;
        margin-top: 6px;
    }

    .approval-helper-text {
        color: #4d5966;
        margin-bottom: 18px;
    }

    /* Read-only fields in edit mode should look inert, not merely unfocused. */
    .approval-form-shell .form-control[readonly],
    .approval-form-shell .form-control:disabled {
        background: #f4f6f8;
        cursor: not-allowed;
    }

    .quote-list {
        list-style: none;
        margin: 10px 0 0 0;
        padding: 0;
    }

    .quote-list li {
        background: #f4f6f8;
        border: 1px solid #dde3e9;
        border-radius: 4px;
        font-size: 13px;
        margin-bottom: 6px;
        padding: 8px 12px;
    }

    .quote-remove {
        color: #9d2525;
        float: right;
        font-size: 12px;
        font-weight: 600;
    }

    .approval-alert {
        border-radius: 4px;
        font-size: 14px;
        margin-bottom: 18px;
        padding: 12px 14px;
    }

    .approval-alert-success {
        background: #edf8f0;
        border: 1px solid #b9e4c4;
        color: #246b35;
    }

    .approval-alert-error {
        background: #fff1f1;
        border: 1px solid #f0baba;
        color: #9d2525;
    }

    @media (max-width: 767px) {
        .approval-form-shell {
            margin-top: 18px;
            padding: 22px 18px 20px;
        }

        .approval-form-grid {
            margin-left: -8px;
            margin-right: -8px;
        }

        .approval-field,
        .approval-actions {
            padding-left: 8px;
            padding-right: 8px;
        }
    }
</style>



// WEBPACK FOOTER //
// src/components/ApprovalForms/StockBuildRequestFormSubmission.vue